import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Pencil } from "lucide-react";
import adAccountsService from "../../services/adAccountsService";
import toast from "react-hot-toast";

export default function ChangeAdAccountNameModal({ isOpen, onClose, account, onSuccess, onSubmit }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const contentRef = useRef(null);

  const rawCurrent = account?.account_name ?? "";
  const trimmed = useMemo(() => name.trim(), [name]);
  const isUnchanged = useMemo(() => trimmed === (rawCurrent || "").trim(), [trimmed, rawCurrent]);
  const isValid = trimmed.length >= 2 && trimmed.length <= 80;

  // derive a usable id from various shapes
  const derivedId = account?._id || account?.id || account?.adAccountId || "";

  useEffect(() => {
    if (!isOpen) return;
    setName(rawCurrent);
    setError("");
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "Enter") handleSubmit(e);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, rawCurrent, derivedId]);

  const onOverlayClick = (e) => {
    if (!contentRef.current) return;
    if (!contentRef.current.contains(e.target)) onClose?.();
  };



  const handleSubmit = async () => {
    setLoading(true)

    try {
      const response = await adAccountsService.changeName(account._id, { name: trimmed })
      // same endpoint, backend handles logic
      const responseData = response.data

      if (responseData?.statusCode === 200) {
        setLoading(true)
        toast.success(`Name changed successfully`)

        setTimeout(() => {
          if (onSuccess) onSuccess()
          onClose()
        }, 1500)
      }
    } catch (error) {
      console.error(`Failed to change the name of the ad account:`, error)
      setError({
        api: error.response?.data?.message || `Failed to change the name of the ad account`,
      })
      toast.error(error.response?.data?.message || `Failed to change the name of the ad account`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onMouseDown={onOverlayClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            ref={contentRef}
            className="w-full max-w-md text-start rounded-2xl border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 shadow-xl"
            onMouseDown={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.98 }}
            transition={{ duration: 0.18 }}
          >
            <form onSubmit={handleSubmit} className="p-5 text-start">
              {/* Header */}
              <div className="mb-4">
                <h2 className="text-base font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-neutral-500" /> Rename Ad Account
                </h2>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  Choose a clear, recognizable name. You can change this later.
                </p>
              </div>

              {/* Field */}
              <div className="space-y-2">
                <label htmlFor="ad-account-name" className="block text-xs font-medium text-neutral-500 dark:text-neutral-400">
                  New name
                </label>
                <input
                  id="ad-account-name"
                  ref={inputRef}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Main – MENA Performance"
                  maxLength={120}
                  aria-invalid={!isValid}
                  aria-describedby="name-help name-error"
                  className="h-10 w-full rounded-xl border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 px-3 text-sm text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 dark:focus:ring-yellow-500/40 focus:ring-yellow-500"
                />
                <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
                  <span id="name-help">2–80 characters. No leading/trailing spaces.</span>
                  <span>{trimmed.length}/80</span>
                </div>
                {!isValid && (
                  <p id="name-error" className="text-xs text-red-600">
                    Name must be between 2 and 80 characters.
                  </p>
                )}
                {error && <p className="text-sm text-red-600">{error}</p>}
              </div>

              {/* Footer */}
              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="h-9 rounded-xl px-3 text-sm border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-950 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !isValid || isUnchanged || !derivedId}
                  className="h-9 rounded-xl px-3 text-sm text-white bg-neutral-900 dark:bg-neutral-100 dark:text-black hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {loading ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ------------------------------------------------------------------
   Example usage inside your AdAccountDetailsEnhanced (React Router page)
   ------------------------------------------------------------------

   // 1) State + trigger button near "Ad Account Name" row
   import ChangeAdAccountNameModal from "./ChangeAdAccountNameModal";

   function BasicInfoSection({ account, onRenamed }) {
     const [renameOpen, setRenameOpen] = useState(false);

     return (
       <div className="p-6 rounded-xl border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 shadow-sm">
         <h3 className="mb-4 text-md font-normal text-neutral-900 dark:text-white">Basic Information</h3>
         <div className="space-y-3">
           <div className="flex justify-between items-center">
             <span className="text-sm text-neutral-500 dark:text-neutral-400">Ad Account Name:</span>
             <div className="flex items-center gap-2">
               <span className="text-sm font-medium text-neutral-900 dark:text-white">{account?.account_name || "-"}</span>
               <button
                 onClick={() => setRenameOpen(true)}
                 className="h-8 px-2 text-xs rounded-xl border border-neutral-200 dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800"
               >
                 Rename
               </button>
             </div>
           </div>
         </div>

         <ChangeAdAccountNameModal
           isOpen={renameOpen}
           onClose={() => setRenameOpen(false)}
           account={account}
           onSuccess={(newName) => onRenamed?.(newName)}
           // Optional: override submit
           // onSubmit={async ({ id, name }) => {
           //   await axiosInstance.patch(`/ad-accounts/update/${id}`, { account_name: name });
           // }}
         />
       </div>
     );
   }
*/
